import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'

const SAFE_LINK_REGEX = /^(https?:\/\/|mailto:|tel:)/i

export const TEXT_ALIGNMENTS = ['left', 'center', 'right', 'justify']

/**
 * Allineamento del testo per blocco (paragrafi e titoli).
 *
 * Emette `style="text-align: …"` inline: e' l'unica forma che sopravvive sia
 * nei client email (Outlook compreso) sia in una pagina che non carica CSS
 * dedicato. Chi salva l'HTML lato server deve ammettere quello stile e nulla
 * di piu'. Legge anche il vecchio attributo `align` per contenuti importati.
 * Equivalente a @tiptap/extension-text-align, senza aggiungere una dipendenza.
 */
export const TextAlign = Extension.create({
  name: 'textAlign',

  addOptions() {
    return {
      types: ['heading', 'paragraph'],
      alignments: TEXT_ALIGNMENTS
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textAlign: {
            default: null,
            parseHTML: (element) => {
              const value = String(element.style?.textAlign || element.getAttribute('align') || '').toLowerCase()
              return this.options.alignments.includes(value) ? value : null
            },
            renderHTML: (attributes) => {
              if (!attributes.textAlign) return {}
              return { style: `text-align: ${attributes.textAlign}` }
            }
          }
        }
      }
    ]
  },

  addCommands() {
    return {
      setTextAlign: (alignment) => ({ commands }) => {
        if (!this.options.alignments.includes(alignment)) return false
        return this.options.types
          .map((type) => commands.updateAttributes(type, { textAlign: alignment }))
          .every(Boolean)
      },
      unsetTextAlign: () => ({ commands }) =>
        this.options.types
          .map((type) => commands.resetAttributes(type, 'textAlign'))
          .every(Boolean)
    }
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Shift-l': () => this.editor.commands.setTextAlign('left'),
      'Mod-Shift-e': () => this.editor.commands.setTextAlign('center'),
      'Mod-Shift-r': () => this.editor.commands.setTextAlign('right'),
      'Mod-Shift-j': () => this.editor.commands.setTextAlign('justify')
    }
  }
})

export function buildExtensions({ placeholder, extraExtensions = [] } = {}) {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      codeBlock: false
    }),
    Underline,
    TextAlign,
    Link.configure({
      openOnClick: false,
      autolink: true,
      validate: (href) => SAFE_LINK_REGEX.test(href),
      HTMLAttributes: {
        rel: 'noopener noreferrer nofollow',
        target: '_blank'
      }
    }),
    Placeholder.configure({
      placeholder: placeholder || ''
    }),
    ...extraExtensions
  ]
}
